import React, { useState, useEffect } from 'react';
import { 
  FaUserShield, 
  FaPercent, 
  FaCreditCard, 
  FaBell,
  FaPalette,
  FaSave,
  FaPlus,
  FaTrash,
  FaEdit
} from 'react-icons/fa';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import { toast } from 'react-toastify';
import './Settings.css';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('commission');
  const [loading, setLoading] = useState(false);
  
  const [commissionSettings, setCommissionSettings] = useState({
    defaultCommission: 0,
    customerGatewayFee: 5,
    providerGatewayFee: 5,
    categories: []
  });

  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true
  });

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const [commissionRes, customerFeeRes, providerFeeRes, notifyRes] = await Promise.all([
        api.get(ENDPOINTS.SETTINGS.commission.default),
        api.get(ENDPOINTS.SETTINGS.gateway.customer),
        api.get(ENDPOINTS.SETTINGS.gateway.provider),
        api.get(ENDPOINTS.SETTINGS.notifications)
      ]);

      const commissionData = commissionRes.data || commissionRes;
      const customerFeeData = customerFeeRes.data || customerFeeRes;
      const providerFeeData = providerFeeRes.data || providerFeeRes;
      const notifyData = notifyRes.data || notifyRes;

      setCommissionSettings(prev => ({
        ...prev,
        defaultCommission: commissionData?.value || 0,
        customerGatewayFee: customerFeeData?.value || 5,
        providerGatewayFee: providerFeeData?.value || 5,
      }));

      if (notifyData && Array.isArray(notifyData)) {
        const notifyState = {};
        notifyData.forEach(s => {
          notifyState[s.key] = s.value === 'true';
        });
        setNotificationSettings(prev => ({ ...prev, ...notifyState }));
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSaveCommission = async () => {
    setLoading(true);
    try {
      await Promise.all([
        api.patch(ENDPOINTS.SETTINGS.commission.default, { value: commissionSettings.defaultCommission.toString() }),
        api.patch(ENDPOINTS.SETTINGS.gateway.customer, { value: commissionSettings.customerGatewayFee.toString() }),
        api.patch(ENDPOINTS.SETTINGS.gateway.provider, { value: commissionSettings.providerGatewayFee.toString() }),
      ]);
      toast.success('Financial settings saved!');
    } catch (error) {
      console.error('Failed to save commission:', error);
      toast.error('Error saving settings.');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'commission', label: 'Commission Settings', icon: <FaPercent /> },
    { id: 'notifications', label: 'Notification Rules', icon: <FaBell /> },
    { id: 'branding', label: 'Branding Assets', icon: <FaPalette /> }
  ];

  return (
    <div className="settings-page">
      <div className="page-header">
        <div>
          <h1>Settings</h1>
          <p className="page-subtitle">Configure platform settings and preferences</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={handleSaveCommission} disabled={loading}>
            <FaSave /> {loading ? 'Saving...' : 'Save All Changes'}
          </button>
        </div>
      </div>

      <div className="container-fluid px-0">
        <div className="row">
          <div className="col-12 col-lg-3 mb-4">
            <div className="settings-sidebar w-100 shadow-sm border rounded p-2 bg-white">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  className={`settings-tab-btn w-100 text-start d-flex align-items-center gap-3 p-3 border-0 rounded transition-all ${activeTab === tab.id ? 'active bg-primary text-white' : 'bg-transparent text-secondary'}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="col-12 col-lg-9">
            <div className="settings-content">
          {activeTab === 'commission' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Commission Settings</h2>
                <p>Configure platform commission rates</p>
              </div>

              <div className="content-card">
                <h3 className="card-title">Default Commission</h3>
                <div className="form-group">
                  <label>Standard Commission Rate (%)</label>
                  <input
                    type="number"
                    value={commissionSettings.defaultCommission}
                    onChange={(e) => setCommissionSettings({
                      ...commissionSettings,
                      defaultCommission: e.target.value
                    })}
                    className="form-control"
                    min="0"
                    max="100"
                    step="0.5"
                  />
                  <small className="form-hint">Applied to all bookings by default</small>
                </div>
              </div>

              <div className="content-card mt-4">
                <h3 className="card-title">Payment Gateway Fees</h3>
                <div className="row">
                  <div className="col-12 col-md-6 mb-3">
                    <div className="form-group">
                      <label>Customer Surcharge (%)</label>
                      <input
                        type="number"
                        value={commissionSettings.customerGatewayFee}
                        onChange={(e) => setCommissionSettings({
                          ...commissionSettings,
                          customerGatewayFee: e.target.value
                        })}
                        className="form-control"
                        min="0"
                        max="100"
                        step="0.1"
                      />
                      <small className="form-hint">Added to customer total at checkout</small>
                    </div>
                  </div>
                  <div className="col-12 col-md-6 mb-3">
                    <div className="form-group">
                      <label>Provider Deduction (%)</label>
                      <input
                        type="number"
                        value={commissionSettings.providerGatewayFee}
                        onChange={(e) => setCommissionSettings({
                          ...commissionSettings,
                          providerGatewayFee: e.target.value
                        })}
                        className="form-control"
                        min="0"
                        max="100"
                        step="0.1"
                      />
                      <small className="form-hint">Deducted from provider during withdrawal</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Notification Rules</h2>
                <p>Configure automated notification triggers</p>
              </div>

              <div className="content-card">
                <h3 className="card-title">Notification Channels</h3>
                <div className="notification-toggles">
                  {Object.keys(notificationSettings).map(key => (
                    <div className="toggle-item" key={key}>
                      <div className="toggle-info">
                        <h4>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</h4>
                      </div>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={notificationSettings[key]}
                          onChange={(e) => setNotificationSettings({
                            ...notificationSettings,
                            [key]: e.target.checked
                          })}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};

export default Settings;